import { Button, DocumentCheckbox, Header, LoadingSpinner } from '@components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { actionReport } from '@methods/actionReport'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'

import { AcceptanceDocument, AcceptanceDocumentRes } from '../types/acceptanceDocument'
import { API_URL } from '@constants/apiUrl'
import { CheckedEvent } from '@/types/generic'
import { cx } from '@/utils/cx'

export const GeneralConditionsPage = observer(() => {
  const store = useContext(StoreContext)
  const { context, currentScenarioId } = store.ScenarioState
  const { generalConditionsPage: trans } = store.TranslationsState.translationsNew

  // Checkboxes state
  const [terms, setTerms] = useState(false)
  const [policy, setPolicy] = useState(false)
  const [biometrics, setBiometrics] = useState(false)
  // const [tickAll, setTickAll] = useState(false)

  // Documents fetched objects
  const [termsDoc, setTermsDoc] = useState<AcceptanceDocument>()
  const [policyDoc, setPolicyDoc] = useState<AcceptanceDocument>()
  const [biometricsDoc, setBiometricsDoc] = useState<AcceptanceDocument>()

  // Loadings state
  const [loading, setLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)

  const getDocuments = async () => {
    try {
      const res = await axiosInstance.get<AcceptanceDocumentRes>(
        `${API_URL}/electronic-id/acceptance-document`,
        { withCredentials: true }
      )

      const privacyPolicy = res.data.find((item) => item.type === 'PRIVACY_POLICY')
      const termsAndConditions = res.data.find((item) => item.type === 'TERMS_AND_CONDITIONS')
      const biometrics = res.data.find((item) => item.type === 'BIOMETRIC_CONSENT')

      setTermsDoc(termsAndConditions)
      setPolicyDoc(privacyPolicy)
      setBiometricsDoc(biometrics)

      setLoading(false)
    } catch (e) {
      devLog(e)
    }
  }

  const handleProceed = async () => {
    if (!(terms && policy)) return

    try {
      if (currentScenarioId !== 'localTests') {
        setBtnLoading(true)

        await axiosInstance.post(
          `${API_URL}/electronic-id/acceptance-document`,
          {
            acceptedDocuments: biometrics
              ? [policyDoc?.id, termsDoc?.id, biometricsDoc?.id]
              : [policyDoc?.id, termsDoc?.id],
          },
          {
            withCredentials: true,
          }
        )

        actionReport({
          type: 'event.onboarding-web.consent.CONSENT_ACCEPTED',
          payload: {},
        })

        setBtnLoading(false)
      }

      store.ScenarioState.nextPage()
    } catch (e) {
      devLog(e)
    }
  }

  const handleTerms = ({ checked: value }: CheckedEvent) => {
    setTerms(value)

    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'TERMS_AND_CONDITIONS', isRequired: true },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'TERMS_AND_CONDITIONS', isRequired: true },
      })
    }
  }

  const handlePolicy = ({ checked: value }: CheckedEvent) => {
    setPolicy(value)

    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'PRIVACY_POLICY', isRequired: true },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'PRIVACY_POLICY', isRequired: true },
      })
    }
  }

  const handleBiometrics = ({ checked: value }: CheckedEvent) => {
    setBiometrics(value)

    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'BIOMETRIC_CONSENT', isRequired: false },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'BIOMETRIC_CONSENT', isRequired: false },
      })
    }
  }

  //  Tick all functionality
  // const handleTickAll = () => {
  //   setTickAll(!tickAll)
  //   setBiometrics(!tickAll)
  //   setTerms(!tickAll)
  //   setPolicy(!tickAll)
  // }

  useEffect(() => {
    if (context?.termsAndConditionsPresent) {
      store.ScenarioState.nextPage()
    } else if (window.location.pathname.split('/')[1] !== 'mock') {
      getDocuments()
    }
  }, [])

  return (
    <div className={cx('page-container', 'mobile-m-0', 'mobile-p-14-40')}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Header>{trans.header}</Header>

          {termsDoc?.id && (
            <DocumentCheckbox
              title={termsDoc?.title}
              inputName="terms-checkbox"
              description={termsDoc?.agreement}
              externalUrl={termsDoc?.s3Url}
              handleDecision={handleTerms}
              value={terms}
              goToDocumentLabel={trans.goToDocument}
            />
          )}
          {policyDoc?.id && (
            <DocumentCheckbox
              title={policyDoc?.title}
              showAsterisk
              isRequired
              inputName="privacy-checkbox"
              description={policyDoc?.agreement}
              externalUrl={policyDoc?.s3Url}
              handleDecision={handlePolicy}
              value={policy}
              goToDocumentLabel={trans.goToDocument}
            />
          )}
          {biometricsDoc?.id && (
            <DocumentCheckbox
              title={biometricsDoc?.title}
              inputName="biometrics-checkbox"
              description={biometricsDoc?.agreement}
              externalUrl={biometricsDoc?.s3Url}
              handleDecision={handleBiometrics}
              value={biometrics}
              goToDocumentLabel={trans.goToDocument}
            />
          )}

          <div className="layout__content-container-bottom-fixed">
            <div className="generic__required-footer">{trans.required}</div>

            <Button onClick={handleProceed} disabled={!(terms && policy)} id="page-submit">
              {btnLoading && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  btnLoading && 'generic__button-text--loading'
                )}
              >
                {trans.proceed}
              </span>
            </Button>
          </div>
        </>
      )}
    </div>
  )
})
